"use client";

import Image from "next/image";
import { useState, useEffect, useRef, useCallback } from "react";
import { Check, Lock, ChevronRight, Sparkles } from "lucide-react";
import { useRims } from "@/hooks/use-rims";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { RimSelector } from "@/components/app/rim-selector";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "@/components/ui/drawer";
import { X } from "lucide-react";
import type { Rim } from "@/lib/db/schema";
import { trackMoreWheelsClicked, trackMoreWheelsDrawerToggled } from "@/lib/posthog-client";

interface FeaturedWheelPickerProps {
  selectedRimId: string | null;
  selectedUserRimId: string | null;
  onSelectRim: (rimId: string | null, userRimId: string | null) => void;
  disabled?: boolean;
  hasPurchasedAnyPack?: boolean;
  className?: string;
}

const FEATURED_COUNT = 8;

export function FeaturedWheelPicker({
  selectedRimId,
  selectedUserRimId,
  onSelectRim,
  disabled = false,
  hasPurchasedAnyPack = false,
  className,
}: FeaturedWheelPickerProps) {
  const [showFullCatalog, setShowFullCatalog] = useState(false);
  const { data: allRims, isLoading } = useRims();
  const scrollRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(false);
  const isDrawerOpenRef = useRef(false);
  const setDrawerOpen = useCallback((open: boolean, reason?: "user" | "pagehide" | "unmount") => {
    if (!isMountedRef.current) {
      return;
    }
    isDrawerOpenRef.current = open;
    setShowFullCatalog(open);
    trackMoreWheelsDrawerToggled({
      action: open ? "open" : "close",
      reason,
      selectedWheelId: selectedRimId || selectedUserRimId || undefined,
      selectedWheelType: selectedUserRimId ? "custom" : selectedRimId ? "catalog" : undefined,
    });
  }, [selectedRimId, selectedUserRimId]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      if (isDrawerOpenRef.current) {
        trackMoreWheelsDrawerToggled({
          action: "close",
          reason: "unmount",
          selectedWheelId: selectedRimId || selectedUserRimId || undefined,
          selectedWheelType: selectedUserRimId ? "custom" : selectedRimId ? "catalog" : undefined,
        });
      }
      isMountedRef.current = false;
    };
  }, [selectedRimId, selectedUserRimId]);

  useEffect(() => {
    const handlePageHide = () => {
      setDrawerOpen(false, "pagehide");
    };
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [setDrawerOpen]);


  // Sort rims: freepreview rims first, then by usage count
  const rims = allRims
    ? [...allRims].sort((a, b) => {
        // Freepreview rims come first
        if (a.freepreview && !b.freepreview) return -1;
        if (!a.freepreview && b.freepreview) return 1;
        // Then by generation count (most used first)
        return (b.generationsCount || 0) - (a.generationsCount || 0);
      })
    : undefined;

  // Get featured rims (first N)
  const featuredRims = rims?.slice(0, FEATURED_COUNT) || [];

  // Auto-select first freepreview wheel if none selected
  useEffect(() => {
    if (rims && rims.length > 0 && !selectedRimId && !selectedUserRimId) {
      const defaultRim = rims.find((r) => r.freepreview) || rims[0];
      if (defaultRim) {
        queueMicrotask(() => {
          if (!isMountedRef.current) {
            return;
          }
          onSelectRim(defaultRim.id, null);
        });
      }
    }
  }, [rims, selectedRimId, selectedUserRimId, onSelectRim]);

  // Auto-scroll to selected wheel
  useEffect(() => {
    if (selectedRimId && scrollRef.current) {
      const selectedElement = scrollRef.current.querySelector(
        `[data-rim-id="${selectedRimId}"]`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [selectedRimId]);

  const handleRimClick = (rim: Rim) => {
    if (disabled) return;
    // If user hasn't purchased any pack, only allow freepreview rims
    if (!hasPurchasedAnyPack && !rim.freepreview) return;
    onSelectRim(rim.id, null);
  };

  const handleDrawerSelect = (rimId: string | null, userRimId: string | null) => {
    if (disabled) {
      return;
    }
    onSelectRim(rimId, userRimId);
    setDrawerOpen(false, "user");
  };

  const handleOpenFullCatalog = () => {
    if (disabled) {
      return;
    }
    trackMoreWheelsClicked({
      selectedWheelId: selectedRimId || selectedUserRimId || undefined,
      selectedWheelType: selectedUserRimId ? "custom" : selectedRimId ? "catalog" : undefined,
    });
    setDrawerOpen(true, "user");
  };

  useEffect(() => {
    if (!disabled || !showFullCatalog) {
      return;
    }
    queueMicrotask(() => {
      if (!isMountedRef.current) {
        return;
      }
      setDrawerOpen(false, "user");
    });
  }, [disabled, showFullCatalog, setDrawerOpen]);

  if (isLoading) {
    return (
      <div className={cn("", className)}>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="shrink-0 w-[96px] h-[96px] bg-white/5 animate-pulse rounded-xl"
              style={{ animationDelay: `${i * 100}ms` }}
            />
          ))}
        </div>
      </div>
    );
  }

  // Check if selected wheel is a custom wheel (user rim)
  const hasCustomWheelSelected = !!selectedUserRimId;

  return (
    <div className={cn("", className)}>
      {/* Section header */}
      <div className="flex items-center justify-end mb-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleOpenFullCatalog}
          className="text-xs text-blue-400 hover:text-blue-300 h-auto py-1 px-2"
        >
          Browse all styles
          <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      </div>

      {/* Custom wheel indicator */}
      {hasCustomWheelSelected && (
        <div className="mb-3 p-2 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-violet-400" />
          <span className="text-xs text-violet-300">Custom wheel selected</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleOpenFullCatalog}
            className="ml-auto text-xs text-violet-400 hover:text-violet-300 h-auto py-0.5 px-2"
          >
            Change
          </Button>
        </div>
      )}

      {/* Horizontal scroll container - p-1 prevents ring clipping */}
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto py-1 px-2 -mx-2 scrollbar-hide snap-x snap-mandatory"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {featuredRims.map((rim, index) => {
          const isLocked = !hasPurchasedAnyPack && !rim.freepreview;
          const isDisabled = disabled || isLocked;
          const isSelected = selectedRimId === rim.id && !hasCustomWheelSelected;

          return (
            <button
              key={rim.id}
              data-rim-id={rim.id}
              onClick={() => handleRimClick(rim)}
              disabled={isDisabled}
              className={cn(
                "group shrink-0 w-[96px] h-[96px] rounded-xl overflow-hidden transition-all duration-300 snap-start",
                "relative bg-linear-to-br from-zinc-800/50 to-zinc-900/50",
                !isDisabled && "hover:scale-[1.05] active:scale-[0.98]",
                isDisabled && "cursor-not-allowed",
                isLocked && "opacity-40",
                isSelected
                  ? "ring-2 ring-blue-500 ring-offset-2 ring-offset-zinc-950 shadow-lg shadow-blue-500/20"
                  : "ring-1 ring-white/10 hover:ring-white/20"
              )}
            >
              {/* Shine effect on hover */}
              {!isDisabled && (
                <div className="absolute inset-0 bg-linear-to-tr from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-1" />
              )}

              <Image
                src={rim.imageUrl}
                alt={rim.name}
                fill
                sizes="96px"
                className={cn(
                  "object-cover transition-transform duration-500",
                  !isDisabled && "group-hover:scale-110",
                  isLocked && "grayscale-[0.3]"
                )}
                loading={index < 4 ? "eager" : "lazy"}
                priority={index < 4}
              />

              {/* Lock overlay for disabled rims */}
              {isLocked && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center backdrop-blur-[1px] z-2">
                  <div className="bg-black/60 rounded-full p-1.5">
                    <Lock className="w-3 h-3 text-white" />
                  </div>
                </div>
              )}

              {/* Selection overlay */}
              {isSelected && !isLocked && (
                <div className="absolute inset-0 bg-blue-500/20 backdrop-blur-[0.5px] pointer-events-none z-3" />
              )}


              {/* Selection indicator */}
              {isSelected && (
                <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full p-0.5 shadow-lg animate-in zoom-in duration-200 z-5">
                  <Check className="w-2.5 h-2.5" strokeWidth={3} />
                </div>
              )}
            </button>
          );
        })}

        {/* More wheels button at the end */}
        <button
          onClick={handleOpenFullCatalog}
          disabled={disabled}
          className={cn(
            "shrink-0 w-[96px] h-[96px] rounded-xl border-2 border-dashed transition-all duration-300 snap-start",
            "flex flex-col items-center justify-center gap-1",
            "bg-white/2 border-white/15",
            !disabled && "hover:border-blue-500/50 hover:bg-blue-500/5 active:scale-95",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        >
          <span className="text-lg font-medium text-zinc-400">+{(rims?.length || 0) - FEATURED_COUNT}</span>
          <span className="text-[9px] text-zinc-500">More</span>
        </button>
      </div>

      {/* Helper text */}
      <p className="text-[10px] text-zinc-500 mt-2">
        Popular styles — swipe to explore
      </p>

      {/* Full catalog drawer */}
      <Drawer
        open={showFullCatalog}
        onOpenChange={(open) => {
          if (disabled) {
            setDrawerOpen(false, "user");
            return;
          }
          setDrawerOpen(open, "user");
        }}
      >
        <DrawerContent className="h-[85vh]">
          <DrawerHeader className="border-b border-white/5 pb-4 shrink-0">
            <div className="flex items-center justify-between">
              <DrawerTitle className="text-lg">All Wheels</DrawerTitle>
              <DrawerClose asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Close wheel catalog">
                  <X className="h-4 w-4" />
                </Button>
              </DrawerClose>
            </div>
          </DrawerHeader>

          <div className="flex-1 overflow-hidden px-4 py-2">
            <RimSelector
              selectedRimId={selectedRimId}
              selectedUserRimId={selectedUserRimId}
              onSelectRim={handleDrawerSelect}
              disabled={disabled}
              hasPurchasedAnyPack={hasPurchasedAnyPack}
            />
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
